import papaparse from "papaparse";
import React, { useEffect, useMemo } from "react";
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { combine, parseDbf, parseShp } from "shpjs";

import UploadFileIcon from '@mui/icons-material/UploadFile';
import { kml } from "@tmcw/togeojson";
import xmldom from "@xmldom/xmldom";
import classNames from "classnames";
import { FeatureCollection } from 'geojson';
import numeral from 'numeral';
import { LocationShapesSourceTypes } from "../core/global";
import { convertCSVToGeoJson, getFileType } from '../helpers/core';
import { toWGS84 } from "../helpers/projections";
import styles from "../styles/components/uploader.module.scss";
import { InfoMessage, WarningMessage } from "./error-message";
import { StyledMarkdown } from "./typeface";

interface UploaderProps {
  isShapeFileUpload?: boolean
  onUploadCompleted: (content: FeatureCollection | null, type: LocationShapesSourceTypes | null, file?: File) => void,
  placeholder?: string
  config?: DropzoneOptions
  returnRaw?: boolean
  hideDescription?: boolean
  onError?: (error: Error) => void
  onDrop?: (acceptedFiles: File[]) => void
  small?: boolean
}

export const Uploader = ({
  isShapeFileUpload,
  onUploadCompleted,
  placeholder = `Drag and drop your KML (.kml), GEO json (.json) or CSV (.csv) file (lon and lat values) here, or click to select a file.`,
  config = {},
  returnRaw = false,
  hideDescription = false,
  onError,
  onDrop,
  small = false,
}: UploaderProps) => {
  const [fileNames, setFileNames] = React.useState<string[]>([])
  const [shapeFileBuffer, setShapeFileBuffer] = React.useState<ArrayBuffer | string | null>(null)
  const [dbfFileBuffer, setDbfFileBuffer] = React.useState<ArrayBuffer | string | null>(null)
  const [projString, setProjString] = React.useState<string | null>(null)

  useEffect(() => {
    setShapeFileBuffer(null)
    setDbfFileBuffer(null)
    setProjString(null)
    setFileNames([])
  }, [])

  useEffect(() => {
    if (shapeFileBuffer && projString && isShapeFileUpload) {
      try {
        const geometries = parseShp(shapeFileBuffer as ArrayBuffer);
        const properties = dbfFileBuffer
          ? parseDbf(dbfFileBuffer as ArrayBuffer, "utf-8" as unknown as ArrayBuffer)
        : [];
        const featureCollection = combine([geometries, properties]) as FeatureCollection;
  
        if (featureCollection) {
          const transformed = toWGS84(featureCollection, projString);
          onUploadCompleted(transformed as FeatureCollection, "shape-file");
        } else {
          onUploadCompleted(featureCollection as FeatureCollection, "shape-file");
        }
      } catch (e) {
        console.error(e);
        onUploadCompleted(null, null);
      }
    }
  }, [shapeFileBuffer, dbfFileBuffer, projString, isShapeFileUpload]);

  const showMismatchingNamesMessage = useMemo(() => {
    if (isShapeFileUpload && fileNames.length === 3) {
      const [first, second, third] = fileNames
      const firstName = first.substring(0, first.lastIndexOf('.')).toLowerCase()
      const secondName = second.substring(0, second.lastIndexOf('.')).toLowerCase()
      const thirdName = third.substring(0, third.lastIndexOf('.')).toLowerCase()
      if (firstName !== secondName || firstName !== thirdName) {
        return true
      }
    }
    return false
  }, [isShapeFileUpload, fileNames])

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps
  } = useDropzone({
      accept: isShapeFileUpload ? {
        'application/vnd.dbf': ['.dbf'],
        '': ['.prj', '.shp'],
      } : {},
      maxSize: 50000,
      maxFiles: isShapeFileUpload ? 3 : 1,
      onDropAccepted: (args) => {
          const files = (args || []).map( (file: any) => file.name)
          setFileNames([...files])
      },
      onError: (error) => {
        // console.error(error)
        if (onError) {
          onError(error)
        }
      },
      onDrop: (acceptedFiles) =>{
        if (onDrop) {
          onDrop(acceptedFiles)
        }
        acceptedFiles.forEach((file: File) => {
          // const isCSV = (file.type === 'text/csv')
          // const isKML = (file.name.toLowerCase().indexOf('.kml') !== -1)
          // const isJSON = (file.name.toLowerCase().indexOf('.json') !== -1)
          const type = getFileType(file)
          // do not upload these files for now
          if (type === 'jpg-file' || type === 'png-file') {
            return
          }
          const reader = new FileReader()
    
          reader.onabort = () => console.log('file reading was aborted')
          reader.onerror = () => console.log('file reading has failed')
          reader.onload = () => {
            try {
              if (type === 'prj-file') {
                const textStr = reader.result
                if (typeof textStr === 'string') {
                  setProjString(textStr)
                }
              } else if (type === 'csv-file') {
                const textStr = reader.result
                if (typeof textStr === 'string') {
                  const csvData = papaparse.parse(textStr)
                  if (csvData && Array.isArray(csvData.data) && csvData.data.length > 0) {
                    // const [first] = csvData.data
                    // let hasHeader = false
                    // if (first && Array.isArray(first) && first.length > 0) {
                    //   const [x] = first[0]
                    //   if ( typeof x === 'string') {
                    //     hasHeader = true
                    //   }
                    // }
                    const useData = csvData.data.filter( row => {
                      if (Array.isArray(row)) {
                        const [x, y] = row
                        return !isNaN(Number(x)) && !isNaN(Number(y))
                      }
                      return false
                    })
                    if (!returnRaw) {
                      const featureCollection = convertCSVToGeoJson(useData as [number, number][], true)
                      onUploadCompleted(featureCollection as FeatureCollection, type)  
                    } else {
                      onUploadCompleted(csvData.data as any, type)
                    }
                    return
                  }
                }
                throw new Error('Cannot read uploaded CSV file')
              } else if (type === 'kml-file') {
                const textStr = reader.result
                const dom = new xmldom.DOMParser().parseFromString(textStr as string, "text/xml")
                // @ts-ignore
                const featureCollection = kml(dom, {});
                // featureCollection.features.map( feature => {
                //   return {
                //     ...feature,
                //     coordinates: feature.coordinates.map()
                //   }
                // })
                onUploadCompleted(featureCollection as FeatureCollection, type)
              } else if (type === 'json-file') {
                const textStr = reader.result
                if (typeof textStr === 'string') {
                  const jsonObject = JSON.parse(textStr)
                  onUploadCompleted(jsonObject as FeatureCollection, type)
                }
              } else if (type === 'shape-file') {
                const isShp = (file.name.toLowerCase().indexOf('.shp') !== -1)
                const isDbf = (file.name.toLowerCase().indexOf('.dbf') !== -1)
        
                // (.shp - only WGS84 is supported at the moment), 
                // const projectionOfData = `PROJCS["WGS_1984_UTM_Zone_30N",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]],PROJECTION["Transverse_Mercator"],PARAMETER["False_Easting",500000.0],PARAMETER["False_Northing",0.0],PARAMETER["Central_Meridian",-3.0],PARAMETER["Scale_Factor",0.9996],PARAMETER["Latitude_Of_Origin",0.0],UNIT["Meter",1.0]]`              
                const binaryBuffer = reader.result
                if (isShp) {
                  setShapeFileBuffer(binaryBuffer)
                }
                if(isDbf) {
                  setDbfFileBuffer(binaryBuffer)
                }
                return
              }
            } catch (e: any) {
              console.error(e)
              onUploadCompleted(null, null)
            }
          }
          if (type === 'shape-file') {
            reader.readAsArrayBuffer(file)
          } else {
            reader.readAsText(file)
          }
        })
      },
    ...config,
  });

  const acceptedFileItems = acceptedFiles.map((file: File) => (
    <li key={file.name}>
      {file.name} - [{numeral(file.size).format('0b')}]
    </li>
  ));

  const fileRejectionItems = fileRejections.map(({ file, errors }) => {
      return (
      <li key={file.name}>
        {file.name} - {numeral(file.size).format('0.00b')}
        <WarningMessage message={errors.map(e => e.message)} />
      </li>
    )
  });

  return (
    <>
    <section className={classNames(styles.uploader, {
      [styles.small]: small
    })}>
      <div {...getRootProps({ className: styles.dropzone })}>
        <input {...getInputProps()} />
        <UploadFileIcon className={styles.icon} />
        {
          isShapeFileUpload ?
          <>
            <p>Drag and drop your GIS shape files (*.shp, *.prj, *.dbf) here, or click to select files. </p>
          </>
          :
          <>
            <StyledMarkdown>
              {placeholder}
            </StyledMarkdown>
          </>
        }
      </div>
      <div className={classNames({
        [styles.hidden]: hideDescription
      })}>
        {
          isShapeFileUpload ?
          <>
          {showMismatchingNamesMessage && <div className={styles.messageWrapper}><WarningMessage message={`The uploaded shape files have **mismatching names**. Please ensure that all files for the shape share the same name (e.g., 'myshape.shp', 'myshape.dbf', 'myshape.prj').`} /></div>}
          {(shapeFileBuffer === null || dbfFileBuffer === null) && 
              <InfoMessage message={`Please upload minimum two files for your shape.  
* **.dbf** contains data shape (optional)  
* **.prj** contains projections  
* **.shp** contains shape  
`} />}
{acceptedFileItems.length > 0 && 
              <>
                <h4>Uploaded file{acceptedFiles.length > 1 ? 's' : ''}</h4>
                <ul>{acceptedFileItems}</ul>
              </>
            }
          </>
          :
          <>
            {acceptedFileItems.length > 0 && 
              <>
                <h4>Uploaded file{acceptedFiles.length > 1 ? 's' : ''}</h4>
                <ul>{acceptedFileItems}</ul>
              </>
            }
          </>
        }
        {fileRejectionItems.length > 0 &&
          <>
            <h4>Rejected file{fileRejections.length > 1 ? 's' : ''}</h4>
            <ul>{fileRejectionItems}</ul>
          </>
        }
      </div>
    </section>
    </>
  )
}